<div class="fs2">
  <div class="fs2-header">
    <div class="fs2-header__icon"><mat-icon class="material-icons-outlined">settings</mat-icon></div>
    <div class="fs2-header__title">{{ "campaign.widget.sidebar.form-settings.title" | translate }}</div>
  </div>

  <div class="fs2-body">
    @if (loading()) {
      <div class="fs2-loading"><mat-spinner diameter="32" mode="indeterminate"></mat-spinner></div>
    } @else if (errorLoading()) {
      <div class="fs2-error">
        <mat-icon>warning</mat-icon>
        <span>{{ "campaign.widget.sidebar.form-settings.error-on-loading-automation-data" | translate }}</span>
      </div>
    } @else {
      <!-- Destinations (response lists, integrations, eCommerce, leaderboard) don't apply to templates. -->
      @if (!isTemplate) {
        <div class="fs2-section">
          <div class="fs2-section__label">{{ "campaign.widget.sidebar.form-settings.send-form-data-to" | translate }}</div>

          <app-flyout-form-settings-response-lists [widget]="showMessageWidget"></app-flyout-form-settings-response-lists>

          @if (hasNoApps()) {
            <div class="fs2-empty">
              <span>{{ "campaign.widget.sidebar.form-settings.no-apps-to-configure" | translate }}</span>
              <button mat-stroked-button class="secondary-button fs2-empty__cta" (click)="connectApps()">
                {{ "campaign.widget.sidebar.form-settings.connect-your-app" | translate | uppercase }}
              </button>
            </div>
          }

          @for (card of appCards(); track card.app.name) {
            <ng-container [ngTemplateOutlet]="appCard" [ngTemplateOutletContext]="{ $implicit: card }"></ng-container>
          }

          <!-- eCommerce registration (Centra/Shopify) — only renders when connected or already configured. -->
          <app-flyout-form-settings-ecommerce
            [widget]="showMessageWidget"
            [widgetState]="showMessageWidgetState"
            [account]="showMessageAccount"
            [existingAutomation]="showMessageAutomation"
            [campaignName]="sectionCampaignName"
            (registrationChanged)="onEcommerceRegistrationChanged()"></app-flyout-form-settings-ecommerce>

          <!-- Leaderboard destination (only renders when the account has leaderboards). -->
          <app-flyout-form-settings-leaderboard
            [widget]="showMessageWidget"
            (dataSourceChanged)="onLeaderboardChanged()"></app-flyout-form-settings-leaderboard>
        </div>
      }

      @if (showMessageWidget) {
        <app-flyout-form-settings-form-options [widget]="showMessageWidget"></app-flyout-form-settings-form-options>
        <app-flyout-form-settings-show-message
          [widget]="showMessageWidget"
          [widgetState]="showMessageWidgetState"
          [account]="showMessageAccount"
          [existingAutomation]="showMessageAutomation"
          [configuredApps]="configuredApps()"
          [availableApps]="visibleApps()"
          [updatesExistingContact]="updatesExistingContact()"
          (automationChanged)="onShowMessageAutomationChanged($event)"></app-flyout-form-settings-show-message>
      }
    }
  </div>

  <div class="fs2-footer">
    <div class="fs2-footer__spacer"></div>
    @if (canGoToAutomation()) {
      <button mat-stroked-button class="secondary-button fs2-footer__automation" (click)="openAutomationInNewTab()">
        <mat-icon>open_in_new</mat-icon>
        {{ "campaign.widget.sidebar.form-settings.go-to-automation" | translate | uppercase }}
      </button>
    }
    <button mat-stroked-button class="secondary-button" (click)="closeFlyout()">
      {{ "generic.close" | translate | uppercase }}
    </button>
  </div>
</div>

<ng-template #appCard let-card>
  <div class="fs2-app" [class.fs2-app--expanded]="expandedAppName() === card.app.name" [class.fs2-app--configured]="card.app.enabled && !card.app.isDraft">
    <div class="fs2-app__header" (click)="card.app.enabled ? toggleExpand(card.app) : enableApp(card.app)">
      <img class="fs2-app__logo" [src]="card.app.iconUrl" [ngClass]="{ reverseColor: card.app | getColorAutomationIcons }" alt="" />
      <div class="fs2-app__name">
        {{ card.app.displayName || (card.translationName ? ("automations.actions.groups." + card.translationName | translate) : card.app.name) }}
        @if (card.app.isBeta) {
          <span class="fs2-app__beta">BETA</span>
        }
        @if (card.app.isNew) {
          <span class="fs2-app__beta">NEW</span>
        }
        @if (card.adminOnly) {
          <span class="fs2-app__admin">A</span>
        }
      </div>
      <div class="fs2-app__spacer"></div>
      @if (card.app.errorKey) {
        <span class="fs2-app__error">
          <mat-icon>error</mat-icon>
          {{ "settings.apps.authenticationEndpointError" | translate }}
        </span>
      } @else if (card.app.isAuthenticated === false) {
        <span class="fs2-app__error">
          <mat-icon>stop_circle</mat-icon>
          {{ "settings.apps.authenticationError" | translate }}
        </span>
      } @else if (card.app.enabled) {
        @if (!card.app.isDraft) {
          <span class="fs2-app__active">
            <mat-icon class="fs2-app__status">check</mat-icon>
            {{ "campaign.widget.sidebar.form-settings-v2.active-badge" | translate }}
          </span>
          <!-- Only when collapsed; the in-body Remove covers the expanded case. -->
          @if (expandedAppName() !== card.app.name) {
            <button
              mat-icon-button
              class="fs2-app__remove"
              [disabled]="savingAppName() === card.app.name || card.realTimeLocked"
              [matTooltip]="(card.realTimeLocked ? 'automations.disable-turn-off-when-real-time' : 'generic.remove') | translate"
              (click)="$event.stopPropagation(); removeApp(card.app)">
              <mat-icon>delete_outline</mat-icon>
            </button>
          }
        }
        <mat-icon class="fs2-app__chevron">{{ expandedAppName() === card.app.name ? "expand_less" : "expand_more" }}</mat-icon>
      } @else {
        <mat-icon class="fs2-app__add">add</mat-icon>
      }
    </div>

    @if (expandedAppName() === card.app.name) {
      <div class="fs2-app__body" @expandReveal>
        @if (card.addGroup; as addGroup) {
          <div class="fs2-contact-section">
            @if (card.supportsFork) {
              <div class="fs2-fork">
                <div class="fs2-fork__label">
                  {{ "campaign.widget.sidebar.form-settings-v2.add-or-update-contact" | translate }}
                </div>
                <mat-button-toggle-group
                  class="fs2-fork__toggle"
                  hideSingleSelectionIndicator="true"
                  [value]="card.contactMode"
                  (change)="setContactMode(card.app.name, $event.value)">
                  <mat-button-toggle [value]="ContactMode.Add">
                    {{ "campaign.widget.sidebar.form-settings-v2.add-contact" | translate }}
                  </mat-button-toggle>
                  <mat-button-toggle [value]="ContactMode.Update">
                    {{ "campaign.widget.sidebar.form-settings-v2.update-contact" | translate }}
                  </mat-button-toggle>
                </mat-button-toggle-group>
                @if (card.updateList; as updateList) {
                  <div class="fs2-fork__list-field">
                    <div class="fs2-fork__list-label">
                      <span>
                        {{ updateList.labelKey | translate }}
                        <span class="fs2-required-marker" aria-hidden="true">*</span>
                      </span>
                    </div>
                    <mat-form-field class="fs2-fork__list" appearance="outline">
                      <mat-select
                        [value]="updateList.value"
                        [multiple]="updateList.multiple"
                        [disabled]="!updateList.options.length"
                        [placeholder]="updateList.labelKey | translate"
                        (selectionChange)="onUpdateListChange(card.app, $event.value)">
                        @if (!updateList.multiple) {
                          <mat-option>{{ "generic.none" | translate }}</mat-option>
                        }
                        @for (option of updateList.options; track option.id) {
                          <mat-option [value]="option.id">{{ option.name }}</mat-option>
                        }
                      </mat-select>
                      @if (!updateList.hasValue) {
                        <mat-hint class="fs2-list-error">
                          {{ "automations.errorMessages.List-is-required" | translate }}
                        </mat-hint>
                      }
                      @if (updateList.missingLabel) {
                        <mat-hint class="fs2-list-error">{{ "automations.tag-not-in-list" | translate }}: {{ updateList.missingLabel }}</mat-hint>
                      }
                    </mat-form-field>
                  </div>
                }
              </div>
            }
            @if (card.addGroupDisabled) {
              <div class="fs2-step__info">{{ card.addGroupDisabledTooltip | translate }}</div>
            } @else {
              @for (a of card.addActions; track $index) {
                @if (a.visible) {
                  <app-action-automation
                    [flyoutOpeningData]="a.item"
                    [nameGroup]="addGroup.nameGroup"
                    [appendDropdownToBody]="true"
                    [widgetStates]="card.widgetStatesContext"
                    [listsIntegrations]="listsIntegrations()"
                    [customProfileFields]="customProfileFields()"
                    [fieldsInOutcomes]="fieldsInOutcomes()"
                    [proceedRequiredListActions]="proceedRequiredListActions()"
                    [realTimeLockFromPersistedMappingOnly]="true"
                    (setPropertyList)="onSetPropertyList($event)"
                    (dynamicModifyingMapFieldInRealTime)="onDynamicMapFieldRealTime($event)"></app-action-automation>
                }
              }
              <!-- The create action's own hint needs its dropdown touched, which it hasn't been after
                   switching over from Update. -->
              @if (card.createListMissing) {
                <div class="fs2-list-error">{{ "automations.errorMessages.List-is-required" | translate }}</div>
              }
              @if (card.addGroupAdditionalInfoKey) {
                <div class="fs2-additional-info" [innerHTML]="'automations.actions.' + card.addGroupAdditionalInfoKey | translate"></div>
              }
            }
          </div>
        }

        <!-- Content is rendered eagerly so opening a step animates to the already-loaded height. -->
        @if (card.rendersFlat) {
          @for (item of card.flatActions; track $index) {
            <app-action-automation
              [flyoutOpeningData]="item"
              [nameGroup]="card.flatNameGroup"
              [appendDropdownToBody]="true"
              [widgetStates]="card.widgetStatesContext"
              [listsIntegrations]="listsIntegrations()"
              [customProfileFields]="customProfileFields()"
              [fieldsInOutcomes]="fieldsInOutcomes()"
              [proceedRequiredListActions]="proceedRequiredListActions()"
              [realTimeLockFromPersistedMappingOnly]="true"
              (setPropertyList)="onSetPropertyList($event)"
              (dynamicModifyingMapFieldInRealTime)="onDynamicMapFieldRealTime($event)"></app-action-automation>
          }
          @if (card.flatAdditionalInfoKey) {
            <div class="fs2-additional-info" [innerHTML]="'automations.actions.' + card.flatAdditionalInfoKey | translate"></div>
          }
        } @else {
          <mat-accordion class="fs2-steps" multi>
            @for (g of card.groups; track g.group.nameGroup) {
              @if (g.visible) {
                <mat-expansion-panel
                  class="fs2-step-panel"
                  [disabled]="g.disabled"
                  [matTooltip]="g.disabledTooltip | translate"
                  [matTooltipDisabled]="!g.disabled"
                  matTooltipPosition="above"
                  (opened)="g.isZapierEmbed && onZapierEmbedPanel(true)"
                  (closed)="g.isZapierEmbed && onZapierEmbedPanel(false)">
                  <mat-expansion-panel-header>
                    <mat-panel-title class="fs2-step__title">
                      {{ "campaign.widget.sidebar.form-settings.config." + g.group.translationKey | translate }}
                      @if (g.isUpdateOtherFields) {
                        <span class="fs2-optional">{{ "generic.optional" | translate }}</span>
                      }
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  @if (g.isZapierEmbed) {
                    <!-- The Zapier widget loads only once its panel opens. -->
                    <div class="fs2-step__info">
                      {{ "campaign.widget.sidebar.form-settings.config.zapier-embed-info" | translate }}
                    </div>
                    @if (zapierEmbedExpanded()) {
                      <app-zapier-embed
                        [introCopyDisplay]="'hide'"
                        [manageZapsDisplay]="'show'"
                        [appSelectionDisplay]="'row'"
                        [appSearchBarDisplay]="'show'"
                        [zapCreateFromScratchDisplay]="'hide'"
                        [guessZapDisplay]="'hide'"
                        [templateLimit]="3"
                        [templateCtaDisplay]="'show'"></app-zapier-embed>
                    }
                  } @else if (g.isTag) {
                    <app-tag-selector
                      [selectedTag]="g.selectedTag"
                      [tags]="tagsForSelector()"
                      [allowCreate]="g.allowCreate"
                      [allowNone]="true"
                      (selectedTagChange)="onTagStepChange(g.group, $event)"
                      (tagCreated)="onTagCreated($event)"></app-tag-selector>
                    @if (g.profileSwitch) {
                      <mat-slide-toggle
                        class="fs2-step__profile-toggle"
                        [checked]="g.profileSwitch.checked"
                        [disabled]="!g.selectedTag"
                        (change)="onTagProfileSwitchChange(g.group, g.profileSwitch.propName, $event.checked)">
                        {{ g.profileSwitch.labelKey | translate }}
                      </mat-slide-toggle>
                    }
                  } @else {
                    @for (item of g.group.actionsGroup; track $index) {
                      <app-action-automation
                        [flyoutOpeningData]="item"
                        [nameGroup]="g.group.nameGroup"
                        [appendDropdownToBody]="true"
                        [widgetStates]="card.widgetStatesContext"
                        [listsIntegrations]="listsIntegrations()"
                        [customProfileFields]="customProfileFields()"
                        [fieldsInOutcomes]="fieldsInOutcomes()"
                        [proceedRequiredListActions]="proceedRequiredListActions()"
                        [realTimeLockFromPersistedMappingOnly]="true"
                        (setPropertyList)="onSetPropertyList($event)"
                        (dynamicModifyingMapFieldInRealTime)="onDynamicMapFieldRealTime($event)"></app-action-automation>
                    }
                  }
                  @if (g.additionalInfoKey) {
                    <div class="fs2-additional-info" [innerHTML]="'automations.actions.' + g.additionalInfoKey | translate"></div>
                  }
                  @if (g.isZapierTrigger) {
                    <div class="fs2-additional-info" [innerHTML]="'automations.actions.SendToZapier.zapier-additional-info' | translate"></div>
                  }
                </mat-expansion-panel>
              }
            }
          </mat-accordion>
        }

        <div class="fs2-app__actions">
          <!-- No remove during initial setup (an unsaved draft) — collapsing the panel cancels it instead. -->
          @if (!card.app.isDraft) {
            <button
              mat-stroked-button
              class="secondary-button fs2-btn--danger"
              [disabled]="savingAppName() === card.app.name || card.realTimeLocked"
              [matTooltip]="'automations.disable-turn-off-when-real-time' | translate"
              [matTooltipDisabled]="!card.realTimeLocked"
              (click)="removeApp(card.app)">
              {{ "generic.remove" | translate | uppercase }}
            </button>
          }
          <div [matTooltip]="expandedSaveState().blockedTooltipKey | translate" [matTooltipDisabled]="!expandedSaveState().blockedTooltipKey">
            <button mat-stroked-button class="primary-button" [disabled]="!expandedSaveState().canSave" (click)="saveApp(card.app)">
              @if (savingAppName() === card.app.name) {
                <mat-spinner class="spinner" diameter="18" mode="indeterminate"></mat-spinner>
              } @else {
                {{ "generic.save" | translate | uppercase }}
              }
            </button>
          </div>
        </div>
      </div>
    }
  </div>
</ng-template>
